import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Rule, TestRuleResult, ValidationStep, testRule } from '../graphql';
import QRCodeDisplay from './QRCodeDisplay';

interface TestRuleDialogProps {
  open: boolean;
  rule: Rule | null;
  onClose: () => void;
}

const TestRuleDialog: React.FC<TestRuleDialogProps> = ({ open, rule, onClose }) => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestRuleResult | null>(null);
  const [activeStep, setActiveStep] = useState(-1);
  const [latitude, setLatitude] = useState('40.7589');
  const [longitude, setLongitude] = useState('-73.9851');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (rule) {
      setTestResult(null);
      setActiveStep(-1);
      setErrorMessage('');
    }
  }, [rule]);

  const handleRunTest = async () => {
    if (!rule) return;

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      setErrorMessage('Please provide valid latitude and longitude values.');
      return;
    }

    setTesting(true);
    setTestResult(null);
    setActiveStep(0);
    setErrorMessage('');

    try {
      const result = await testRule(rule.ruleId, { latitude: lat, longitude: lng });
      setTestResult(result);

      const completedSteps = result.steps.filter(s => s.status === 'success').length;
      setActiveStep(completedSteps);
    } catch (error: any) {
      setErrorMessage(error.message || 'An unexpected error occurred during testing.');
    } finally {
      setTesting(false);
    }
  };

  const getStepIcon = (step: ValidationStep) => {
    if (step.status === 'success') {
      return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
    }
    if (step.status === 'failed') {
      return <ErrorIcon sx={{ color: '#f44336' }} />;
    }
    return null;
  };

  const renderStepContent = (step: ValidationStep) => {
    if (step.status === 'success') {
      return (
        <Box>
          <Typography variant="body2" sx={{ color: '#4caf50', mb: 1 }}>
            Completed in {step.duration}ms
          </Typography>
          {step.output && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="caption">View Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre style={{ fontSize: '0.75rem', overflow: 'auto', margin: 0 }}>
                  {JSON.stringify(parseJsonField(step.output), null, 2)}
                </pre>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      );
    }

    if (step.status === 'failed') {
      return (
        <Typography variant="body2" sx={{ color: '#f44336' }}>
          {step.error || 'Failed'}
        </Typography>
      );
    }

    return <Typography variant="body2" color="text.secondary">Waiting...</Typography>;
  };

  const parseJsonField = (value: any): any => {
    if (typeof value === 'string') {
      try { return JSON.parse(value); } catch { return value; }
    }
    return value;
  };

  const step2Output = parseJsonField(testResult?.steps.find(s => s.step === 2)?.output);
  const qrUrl = step2Output?.qrUrl;

  if (!rule) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '80vh', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)', fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif", fontWeight: 500 }}>Test Rule: {rule.name}</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* LEFT PANEL */}
          <Box sx={{ flex: { md: '0 0 40%' }, width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                Generates a QR code for this rule using a product code from the rule. Scan the QR code to verify the redirect.
              </Alert>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Location (for redirect validation)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Latitude"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ step: 'any' }}
                  />
                  <TextField
                    label="Longitude"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ step: 'any' }}
                  />
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={handleRunTest}
                disabled={testing}
                fullWidth
                size="large"
                startIcon={testing ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                sx={{ borderRadius: '8px', fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif", textTransform: 'none', fontWeight: 500 }}
              >
                {testing ? 'Testing...' : 'Run Test'}
              </Button>

              <Divider />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
                Test Progress
              </Typography>

              <Stepper activeStep={activeStep} orientation="vertical">
                {(testResult?.steps || [
                  { step: 1, name: 'Get GTIN from Product Library', status: 'pending' as const },
                  { step: 2, name: 'Generate QR Code via IDGen', status: 'pending' as const },
                ]).map((step) => (
                  <Step key={step.step} completed={step.status === 'success'}>
                    <StepLabel
                      optional={getStepIcon(step as ValidationStep)}
                      error={step.status === 'failed'}
                    >
                      {step.name}
                    </StepLabel>
                    <StepContent>
                      {renderStepContent(step as ValidationStep)}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>

              {testResult && testResult.success && (
                <Alert severity="success">
                  {testResult.message}
                </Alert>
              )}

              {testResult && !testResult.success && (
                <Alert severity="error">
                  {testResult.message}
                </Alert>
              )}

              {errorMessage && !testResult && (
                <Alert severity="error">{errorMessage}</Alert>
              )}
            </Box>
          </Box>

          {/* RIGHT PANEL */}
          <Box sx={{ flex: { md: '0 0 60%' }, width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>QR Code & Validation</Typography>

            {!testResult && !testing && (
              <Box
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px dashed #e0e0e0', borderRadius: 2, p: 4, textAlign: 'center', minHeight: 300,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Run the test to generate and validate a QR code
                </Typography>
              </Box>
            )}

            {testing && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, minHeight: 300 }}>
                <CircularProgress size={60} />
                <Typography variant="body1">Testing in progress...</Typography>
              </Box>
            )}

            {testResult && qrUrl && (
              <QRCodeDisplay
                qrUrl={qrUrl}
                redirectUrl={rule.redirectUrl}
              />
            )}

            {testResult && !qrUrl && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4, minHeight: 300 }}>
                <Alert severity="error" sx={{ width: '100%' }}>
                  <Typography variant="body1" sx={{ mb: 1 }}><strong>Test Failed</strong></Typography>
                  <Typography variant="body2">
                    {testResult.message || 'QR code generation failed. Check the step details on the left.'}
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TestRuleDialog;
